import { z } from 'zod';

/**
 * Emotes a viewer has chosen to see, and to be seen using.
 *
 * Third-party emotes live in sets we do not control — a channel's 7TV set, a
 * sender's 7TV personal set — and a word is text for anybody whose client did
 * not load the right one. A saved emote is the viewer saying "this word is
 * this picture": it fills gaps in what they see everywhere, and other tomo
 * viewers see it in that viewer's own messages.
 *
 * **Provider and id, never a URL.** Other people's browsers render these, so
 * a stored URL would let anybody put an arbitrary image — a tracking pixel,
 * something worse — into strangers' chat. Every URL is built from the id by
 * `savedEmoteImages` in `@tomo/chat`, against a CDN we name.
 *
 * Owned by the ACCOUNT and looked up by Twitch user id, never by login:
 * logins change when people rename, and a set keyed on one would follow the
 * name to whoever took it next.
 */

/** Enough for anybody's working set; small enough that one document holds it. */
export const SAVED_EMOTE_MAX = 200;

/** Batch size for looking up other people's saved emotes. */
export const SAVED_EMOTE_LOOKUP_MAX = 100;

export const SAVED_EMOTE_PROVIDERS = ['7tv', 'ffz'] as const;

export const SavedEmote = z.object({
  /**
   * The word it replaces, exactly as typed. Case matters: `catJAM` and
   * `CatJAM` are different emotes. No whitespace, because chat is matched a
   * whitespace-delimited token at a time.
   */
  name: z.string().regex(/^\S{1,64}$/u),
  provider: z.enum(SAVED_EMOTE_PROVIDERS),
  /**
   * The provider's id. 7TV's are 26-character ULIDs (24-hex on old emotes),
   * FFZ's are numbers. Alphanumeric only: it becomes a URL path segment.
   */
  emote_id: z.string().regex(/^[0-9A-Za-z]{1,40}$/),
  /** Who uploaded it — how two emotes with one name are told apart. Display only. */
  owner: z.string().max(64).nullable(),
  /**
   * Which pixel densities exist. FFZ does not generate every size for every
   * emote, and a `srcset` naming one that 404s is a broken image on exactly
   * the displays that asked for the sharp one.
   */
  densities: z.array(z.union([z.literal(1), z.literal(2), z.literal(4)])).min(1).max(3),
});
export type SavedEmote = z.infer<typeof SavedEmote>;

export const SavedEmoteList = z.object({
  data: z.array(SavedEmote).max(SAVED_EMOTE_MAX).refine(
    (list) => new Set(list.map((e) => e.name)).size === list.length,
    { message: 'Each name can be saved once.' },
  ),
});
export type SavedEmoteList = z.infer<typeof SavedEmoteList>;

export const SenderEmotesQuery = z.object({
  /** Comma-separated Twitch user ids — senders in Twitch chat. */
  twitch_ids: z.string().max(2400).optional(),
  /** Comma-separated tomo uids — senders in a room's own chat. */
  uids: z.string().max(4000).optional(),
});

/**
 * Other people's saved emotes, answered in the terms they were asked in.
 *
 * Only people who have saved something appear; everyone else is absent,
 * which is the common case and the cheapest thing to send. The two maps are
 * deliberately separate: answering a Twitch-id question with a uid would
 * publish which Twitch account is which tomo account, and nobody asked that.
 */
export const SenderEmotes = z.object({
  by_twitch_id: z.record(z.string(), z.array(SavedEmote)),
  by_uid: z.record(z.string(), z.array(SavedEmote)),
});
export type SenderEmotes = z.infer<typeof SenderEmotes>;

/** As many as anybody overrules; small enough that one document holds them. */
export const EMOTE_OVERRIDE_MAX = 200;

/**
 * How the viewer wants a word drawn, whatever chat would draw it as.
 *
 * The other side of a saved emote. A saved emote fills a gap and is seen by
 * other people in the saver's messages; an override is a correction and is
 * seen by nobody but the viewer. It wins over everything — the channel's
 * sets, the sender's saved emotes, the viewer's own — because the whole point
 * is "not the one this chat uses".
 *
 * `emote: null` draws the word as text: the emote that looks like something
 * the viewer would rather not see, or a common word some set turned into a
 * picture. Otherwise it is the emote to draw instead, stored exactly as a
 * saved emote is — provider and id, never a URL — and its name must be the
 * word it replaces.
 */
export const EmoteOverride = z.object({
  name: z.string().regex(/^\S{1,64}$/u),
  emote: SavedEmote.nullable(),
}).refine((o) => o.emote === null || o.emote.name === o.name, {
  message: 'An emote drawn instead must be the one with that name.',
});
export type EmoteOverride = z.infer<typeof EmoteOverride>;

export const EmoteOverrideList = z.object({
  data: z.array(EmoteOverride).max(EMOTE_OVERRIDE_MAX).refine(
    (list) => new Set(list.map((o) => o.name)).size === list.length,
    { message: 'Each name can be overridden once.' },
  ),
});
export type EmoteOverrideList = z.infer<typeof EmoteOverrideList>;
